import { createContext, ReactNode, useContext, useState } from "react";
import {
  useQuery,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { insertUserSchema, User as SelectUser, InsertUser } from "@shared/schema";
import { getQueryFn, apiRequest, queryClient } from "../lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { getApiUrl } from "../config";
import { AppAbility, createForUser } from "../lib/ability";
import { fetchUser } from "../data-access/users";
import type { GetUserDto } from "@server/routes/users/dto";
type AuthContextType = {
  user: GetUserDto | null;
  isLoading: boolean;
  error: Error | null;
  loginMutation: UseMutationResult<LoginResponse, Error, LoginData>;
  verifyMfaMutation: UseMutationResult<GetUserDto, Error, MfaVerifyData>;
  logoutMutation: UseMutationResult<void, Error, void>;
  registerMutation: UseMutationResult<RegisterResponse, Error, InsertUser>;
  refreshSession: () => Promise<GetUserDto | null>;
  pendingMfa: boolean;
  mfaUserId: number | null;
  ability: AppAbility;
};

type LoginData = Pick<InsertUser, "username" | "password">;
type MfaVerifyData = { token: string };

interface LoginResponse {
  requiresMfa?: boolean;
  userId?: number;
  username?: string;
  message?: string;
}

// /api/register either returns a real, logged-in user or a stub indicating the
// account still needs email verification (no usable session yet).
type RegisterVerificationPending = {
  requiresVerification: true;
  email: string;
  message?: string;
};
type RegisterResponse = SelectUser | RegisterVerificationPending;

export const AuthContext = createContext<AuthContextType | null>(null);
export function AuthProvider({ children }: { children: ReactNode }) {
  const { toast } = useToast();
  const [pendingMfa, setPendingMfa] = useState(false);
  const [mfaUserId, setMfaUserId] = useState<number | null>(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  
  const {
    data: user,
    error,
    isLoading,
    refetch: refetchUser,
  } = useQuery({
    queryKey: ["/api/user"],
    queryFn: async () => {
      try {
        const user = await fetchUser();
        setIsLoggedIn(true);
        return user;
      } catch (error) {
        if (isLoggedIn) {
          setIsLoggedIn(false);
        }
        // Clear stale user data so ProtectedRoute / consumers see null immediately.
        // The session-expired modal blocks the UI, but defense-in-depth ensures
        // no protected component renders against a phantom authenticated user.
        queryClient.setQueryData(["/api/user"], null);
        throw error;
      }
    },
    // More frequent refetching to prevent session issues
    retry: 1,
    retryDelay: 5000,
    refetchInterval: 1 * 60 * 1000, // Refresh every 1 minute
    refetchOnWindowFocus: true, // Refresh when window regains focus
  });

  const loginMutation = useMutation({
    mutationFn: async (credentials: LoginData) => {
      try {
        const res = await fetch(getApiUrl("api/login"), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(credentials),
          credentials: "include"
        });
        
        if (!res.ok) {
          // Get the error message from the response for all error types
          const errorData = await res.json().catch(() => null);
          const errorMessage = errorData?.message;
          
          if (res.status === 401) {
            // Use the server's specific message for 401 errors (includes lockout messages)
            throw new Error(errorMessage || "Incorrect email or password. Please try again.");
          } else if (res.status === 429) {
            throw new Error(errorMessage || "Too many login attempts. Please try again later.");
          } else {
            // For other errors, get the message from the response if possible
            throw new Error(errorMessage || "Login failed. Please try again later.");
          }
        }
        
        const data = await res.json();
        return data;
      } catch (err) {
        // This captures network errors and our custom error messages
        if (err instanceof Error) {
          throw err;
        }
        throw new Error("Unable to connect to the server. Please check your internet connection.");
      }
    },
    onSuccess: (response: LoginResponse) => {
      // Check if MFA is required
      if (response.requiresMfa && response.userId) {
        setPendingMfa(true);
        setMfaUserId(response.userId);
        toast({
          title: "Two-Factor Authentication Required",
          description: "Please enter the verification code from your authenticator app.",
          variant: "default",
        });
      } else if (!response.requiresMfa) {
        // Normal login without MFA
        refetchUser().then(() => {
          toast({
            title: "Login successful",
            description: "Welcome back!",
            className: "bg-white text-black border-zinc-200",
          });
        });
      }
    },
    onError: (error: Error) => {
      setPendingMfa(false);
      setMfaUserId(null);
      toast({
        title: "Login failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });
  
  // MFA verification mutation
  const verifyMfaMutation = useMutation({
    mutationFn: async (data: MfaVerifyData) => {
      try {
        const res = await fetch(getApiUrl("api/mfa/verify"), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(data),
          credentials: "include"
        });
        
        if (!res.ok) {
          // Handle error responses
          if (res.status === 401) {
            throw new Error("Invalid verification code. Please try again.");
          } else if (res.status === 429) {
            throw new Error("Too many verification attempts. Please try again later.");
          } else {
            // For other errors, get the message from the response if possible
            const errorData = await res.json().catch(() => null);
            throw new Error(errorData?.error || "Verification failed. Please try again.");
          }
        }
        
        return await res.json();
      } catch (err) {
        if (err instanceof Error) {
          throw err;
        }
        throw new Error("Unable to connect to the server. Please check your internet connection.");
      }
    },
    onSuccess: () => {
      // Reset MFA state
      setPendingMfa(false);
      setMfaUserId(null);
      
      // Refetch user data to update the auth state
      refetchUser().then(() => {
        toast({
          title: "Verification successful",
          description: "You are now logged in.",
          variant: "default",
        });
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Verification failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const registerMutation = useMutation({
    mutationFn: async (credentials: InsertUser): Promise<RegisterResponse> => {
      const res = await apiRequest("POST", "/api/register", credentials);
      return await res.json();
    },
    onSuccess: (data: RegisterResponse) => {
      // A registration that requires email verification does NOT create a usable
      // session — the response is a verification stub, not a real user. Writing it
      // into the user cache produces a phantom "logged-in" user that bounces the
      // login page to "/" and crashes protected routes. Only cache real users.
      if (!("requiresVerification" in data)) {
        queryClient.setQueryData(["/api/user"], data);
      }
    },
    onError: (error: Error) => {
      toast({
        title: "Registration failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const logoutMutation = useMutation({
    mutationFn: async () => {
      await apiRequest("POST", "/api/logout");
    },
    onSuccess: () => {
      // Clear all cached data to ensure no stale data remains after logout
      queryClient.clear();

      // redirect to login page
      window.location.href = "/login";
    },
    onError: (error: Error) => {
      toast({
        title: "Logout failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Function to refresh user session
  const refreshSession = async (): Promise<GetUserDto | null> => {
    try {
      // Force a refetch of the user data
      const result = await refetchUser();
      
      if (result.data) {
        return result.data;
      }
      
      return null;
    } catch (error) {
      console.error("Session refresh failed:", error);
      return null;
    }
  };

  const ability = createForUser(user);

  return (
    <AuthContext.Provider
      value={{
        ability,
        user: user ?? null,
        isLoading,
        error,
        loginMutation,
        verifyMfaMutation,
        logoutMutation,
        registerMutation,
        refreshSession,
        pendingMfa,
        mfaUserId
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
